import type { FC, ReactNode } from 'react'
import type { Bubble } from '@/components/landing/concept-screen'
import type { DemoLanguage, StageId } from '@/constants/content'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Eye, Hand, Languages } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'
import { StatusLabel } from '@/components/generals/status-label'
import { ConceptScreen, defaultBubbles } from '@/components/landing/concept-screen'
import { TabletFrame } from '@/components/landing/tablet-frame'

import { demoLanguages, playToPitch } from '@/constants/content'

const AUTO_ADVANCE_MS = 5500
const stageOrder: StageId[] = ['attract', 'engage', 'discover', 'connect']

interface LanguageToggleProps {
    label: string
    value: DemoLanguage
    onChange: (value: DemoLanguage) => void
}

const LanguageToggle: FC<LanguageToggleProps> = ({ label, value, onChange }): ReactNode => (
    <div role='group' aria-label={label} className='flex items-center justify-between gap-3'>
        <span className='text-sm text-cool-grey-light'>
            {label}
        </span>

        <div className='flex rounded-xl bg-navy-950/70 p-1 ring-1 ring-soft-white/10'>
            {demoLanguages.map(language => (
                <button
                    key={language.id}
                    type='button'
                    aria-pressed={value === language.id}
                    aria-label={`${label}: ${language.label}`}
                    onClick={() => onChange(language.id)}
                    className={cn(
                        'min-h-11 min-w-12 rounded-lg px-3 text-sm font-semibold transition',
                        value === language.id ? 'bg-orange text-navy' : 'text-cool-grey-light hover:text-soft-white'
                    )}
                >
                    {language.short}
                </button>
            ))}
        </div>
    </div>
)

const PlayToPitchSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const [stage, setStage] = useState<StageId>('attract')
    const [score, setScore] = useState(120)
    const [bubbles, setBubbles] = useState<Bubble[]>(defaultBubbles)
    const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null)
    const [subtitleLanguage, setSubtitleLanguage] = useState<DemoLanguage>('en')
    const [spokenLanguage, setSpokenLanguage] = useState<DemoLanguage>('en')
    const [muted, setMuted] = useState(false)
    const [autoPlay, setAutoPlay] = useState(true)
    const [inView, setInView] = useState(false)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) setAutoPlay(false)
    }, [])

    useEffect(() => {
        const element = sectionRef.current
        if (!element) return

        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.35 })
        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!autoPlay || !inView) return

        const timer = window.setTimeout(() => {
            setStage(current => stageOrder[(stageOrder.indexOf(current) + 1) % stageOrder.length])
        }, AUTO_ADVANCE_MS)

        return () => window.clearTimeout(timer)
    }, [autoPlay, inView, stage])

    useEffect(() => {
        if (autoPlay && stage === 'discover') setSelectedQuestion('new')
        if (autoPlay && stage === 'attract') setSelectedQuestion(null)
    }, [autoPlay, stage])

    const interact = useCallback((action: () => void) => {
        setAutoPlay(false)
        action()
    }, [])

    const popBubble = (id: number): void => interact(() => {
        setScore(current => current + 10)
        setBubbles(current => current.map(bubble => bubble.id === id
            ? { ...bubble, x: 12 + Math.random() * 70, y: 32 + Math.random() * 32, delay: Math.random() }
            : bubble))
    })

    return (
        <section
            ref={sectionRef}
            id='play-to-pitch'
            aria-labelledby='play-to-pitch-heading'
            className='relative overflow-hidden border-y border-soft-white/10 bg-navy-900 py-24 sm:py-32'
        >
            <div aria-hidden className='absolute top-1/3 right-0 size-[36rem] translate-x-1/3 rounded-full bg-orange/10 blur-[120px]' />

            <div className='container-site relative'>
                <SectionHeading
                    id='play-to-pitch-heading'
                    eyebrow={playToPitch.eyebrow}
                    title={playToPitch.heading}
                    intro={playToPitch.principle}
                    aside={<StatusLabel status='inDevelopment' />}
                />

                <div className='mt-14 grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12'>
                    <div className='order-2 flex flex-col gap-6 lg:order-1'>
                        <ol className='grid gap-3 sm:grid-cols-2 lg:grid-cols-1'>
                            {playToPitch.stages.map(item => {
                                const active = stage === item.id

                                return (
                                    <li key={item.id}>
                                        <button
                                            type='button'
                                            aria-pressed={active}
                                            onClick={() => interact(() => setStage(item.id))}
                                            className={cn(
                                                'group relative flex w-full items-start gap-4 overflow-hidden rounded-xl border p-4 text-left transition duration-300 sm:p-5',
                                                active
                                                    ? 'border-orange/60 bg-navy-700 shadow-[0_20px_50px_-30px_rgb(255_138_61/0.6)]'
                                                    : 'border-soft-white/10 bg-navy-800/60 hover:border-soft-white/25 hover:bg-navy-800'
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'grid size-10 shrink-0 place-items-center rounded-full font-heading text-sm font-bold transition',
                                                    active ? 'bg-orange text-navy' : 'bg-navy-950 text-cool-grey-light ring-1 ring-soft-white/10'
                                                )}
                                            >
                                                {item.number}
                                            </span>

                                            <span className='flex flex-col gap-1'>
                                                <span className='font-heading text-lg font-bold text-soft-white'>
                                                    {item.title}
                                                </span>

                                                <span className='text-sm leading-relaxed text-cool-grey-light'>
                                                    {item.description}
                                                </span>

                                                <span className='mt-1 text-xs font-semibold text-orange'>
                                                    {item.takeaway}
                                                </span>
                                            </span>

                                            {active && autoPlay && inView && (
                                                <span
                                                    key={stage}
                                                    aria-hidden
                                                    className='stage-progress absolute bottom-0 left-0 h-0.5 bg-orange'
                                                    style={{ animationDuration: `${AUTO_ADVANCE_MS}ms` }}
                                                />
                                            )}
                                        </button>
                                    </li>
                                )
                            })}
                        </ol>

                        <div className='rounded-xl border border-soft-white/10 bg-navy-800/60 p-5'>
                            <div className='flex items-center gap-2 text-sm font-semibold text-soft-white'>
                                <Languages className='size-4 text-orange' aria-hidden />
                                Language settings
                                <StatusLabel status='inDevelopment' className='ml-auto' />
                            </div>

                            <div className='mt-4 flex flex-col gap-3'>
                                <LanguageToggle
                                    label='Spoken language'
                                    value={spokenLanguage}
                                    onChange={value => interact(() => setSpokenLanguage(value))}
                                />

                                <LanguageToggle
                                    label='Subtitle language'
                                    value={subtitleLanguage}
                                    onChange={value => interact(() => setSubtitleLanguage(value))}
                                />
                            </div>

                            <p className='mt-4 text-xs leading-relaxed text-cool-grey-light'>
                                Spoken and subtitle languages are separate settings in the planned product. This demonstration plays no audio; the subtitles switch between prewritten English and Bahasa Melayu lines.
                            </p>
                        </div>
                    </div>

                    <div className='order-1 lg:order-2'>
                        <div className='lg:sticky lg:top-28'>
                            <div className='mb-4 flex flex-wrap items-center gap-2'>
                                <StatusLabel status='concept' />

                                <span className='inline-flex items-center gap-1.5 text-xs text-cool-grey-light'>
                                    <Hand className='size-3.5 text-orange' aria-hidden />
                                    {playToPitch.tryIt}
                                </span>
                            </div>

                            <TabletFrame tilt>
                                <div role='region' aria-label='Play-to-Pitch concept demonstration' aria-roledescription='interactive illustration'>
                                    <ConceptScreen
                                        interactive
                                        stage={stage}
                                        language={subtitleLanguage}
                                        spokenLanguage={spokenLanguage}
                                        score={score}
                                        bubbles={bubbles}
                                        muted={muted}
                                        speaking={stage === 'engage' || stage === 'connect'}
                                        selectedQuestion={selectedQuestion}
                                        onPlay={() => interact(() => setStage('engage'))}
                                        onPop={popBubble}
                                        onTapBucky={() => interact(() => setStage('discover'))}
                                        onClose={() => interact(() => setStage('engage'))}
                                        onSelectQuestion={id => interact(() => setSelectedQuestion(id))}
                                        onShowNextStep={() => interact(() => setStage('connect'))}
                                        onBackToGame={() => interact(() => setStage('engage'))}
                                        onToggleMute={() => interact(() => setMuted(current => !current))}
                                    />
                                </div>
                            </TabletFrame>

                            <p className='mt-2 text-center text-xs text-cool-grey-light'>
                                {playToPitch.demoLabel}
                            </p>

                            <div className='mt-6 rounded-xl border border-soft-white/10 bg-navy-800/60 p-5'>
                                <p className='flex items-center gap-2 text-sm font-semibold text-soft-white'>
                                    <Eye className='size-4 text-orange' aria-hidden />
                                    {playToPitch.visitorSees.title}
                                </p>

                                <ul className='mt-3 flex flex-col gap-2 text-sm leading-relaxed text-cool-grey-light'>
                                    {playToPitch.visitorSees.points.map(point => (
                                        <li key={point} className='flex gap-2.5'>
                                            <span aria-hidden className='mt-2 size-1.5 shrink-0 rounded-full bg-orange' />
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default PlayToPitchSection
